import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Trash2, Library, BarChart3, ListChecks, ScrollText, ChevronDown, ChevronRight, ChevronUp, Plus, Inbox, FolderInput, Check, MoreHorizontal, Pencil, Palette, CheckSquare, X, CalendarDays, CalendarRange, Users, Share2, BookOpen, FileText, Printer, ClipboardCheck, Sparkles, Compass, Calculator } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import TrilliumIcon from "../components/TrilliumIcon";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import { getCoverage, listActivities, deleteActivity, listFolders, createFolder, moveActivityToFolder, updateFolder, deleteFolder, deleteFolderWithCascade, moveFolder, reorderFolders, shareActivity, unshareActivity, getActivity } from "../lib/api";
import ExportButtons from "../components/ExportButtons";
import ConfirmDeleteDialog from "../components/ConfirmDeleteDialog";
import MarkTaughtButton from "../components/MarkTaughtButton";
// Phase 107 — Lesson Workspace replaces the inline View modal +
// per-format buttons inside library cards. Imports for the old modal
// (LessonDocument, UnitDocument, ActivityResultCard, Dialog) were
// removed when the card was slimmed to a single "Open" link.
import UserMenu from "../components/UserMenu";
import StartBuildingButton from "../components/StartBuildingButton";
import AppShell from "../components/shell/AppShell";
import CoverageTracker from "../components/CoverageTracker";
import CommunityTab from "../components/CommunityTab";
import CascadeDeleteDialog from "../components/CascadeDeleteDialog";
// Pre-launch banners removed Feb 2026 launch.
import ReportCardGenerator from "../components/ReportCardGenerator";
import Planner from "../components/Planner";
import SkillInTheWildCard from "../components/SkillInTheWildCard";
import SkillInTheWildImage from "../components/SkillInTheWildImage";
// Lane 2 (Feb 09 2026) — subscriber Home restructure. Home is a
// landing surface, not a stats dump: Today → Skill in the Wild →
// Recent work → Rings → Sample Plans, in that order.
import TodayCard from "../components/home/TodayCard";
import HomePlannerPreview from "../components/home/HomePlannerPreview";
import RecentWorkCard from "../components/home/RecentWorkCard";
import SkillRingsCard from "../components/home/SkillRingsCard";
import ScheduleUnitDialog from "../components/ScheduleUnitDialog";
import PostGenScheduleDialog from "../components/PostGenScheduleDialog";
import ShareLessonDialog from "../components/ShareLessonDialog";
import { SubjectTilesBrowse, UnitTilesStrip } from "../components/LibraryBrowse";
import ClassroomSupportPlansSection from "../components/classroom-support/PlansLibrarySection";
import IspsLibrarySection from "../components/IspsLibrarySection";
import SupplyPlansLibrarySection from "../components/SupplyPlansLibrarySection";
import LibrarySectionTabs from "../components/LibrarySectionTabs";
import LibraryRecentStrip from "../components/LibraryRecentStrip";
import FreeHome from "../components/FreeHome";
import ReportCardNudgeBanner from "../components/ReportCardNudgeBanner";
import { useAuth } from "../contexts/AuthContext";
import { isPaidUser, isBasicUser } from "../lib/tier";

const fmt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const sortDesc = (obj) =>
  Object.entries(obj || {}).sort((a, b) => b[1] - a[1]);

// Phase 152.AA — classify an activity into a Library section. Summative
// tasks live in their own section so teachers don't have to scan past
// 30 lessons to find the one assessment they need.
const SUMMATIVE_OUTPUT_TYPES = new Set([
  "summative_task",
  "summative",
  "test",
  "math_test",
]);
const SUMMATIVE_ACTIVITY_TYPES = new Set([
  "summative_assessment",
  "summative",
]);
const sectionForActivity = (a) => {
  const ot = (a?.output_type || "").toLowerCase();
  const at = (a?.activity_type || "").toLowerCase();
  if (SUMMATIVE_OUTPUT_TYPES.has(ot) || SUMMATIVE_ACTIVITY_TYPES.has(at)) {
    return "summative";
  }
  return "lessons";
};

// Feb 2026 — Recently-added strip helper. Normalises an activity row
// into the shape `LibraryRecentStrip` expects (`id` / `title` /
// `subtitle` / `dateLabel` / `href`). Tests deep-link to the Test
// Builder; everything else opens the Lesson Workspace.
const activityToRecentItem = (a) => {
  const ot = (a?.output_type || "").toLowerCase();
  const isTest = ot === "test" && a.linked_test_id;
  const meta = [
    a?.inputs?.grade
      ? (String(a.inputs.grade).toLowerCase().startsWith("grade")
          ? a.inputs.grade
          : `Grade ${a.inputs.grade}`)
      : "",
    a?.inputs?.subject || "",
    a?.inputs?.strand || "",
  ]
    .filter(Boolean)
    .join(" · ");
  let dateLabel = "";
  if (a?.created_at) {
    try {
      dateLabel = new Date(a.created_at).toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      });
    } catch {
      dateLabel = "";
    }
  }
  return {
    id: a.id,
    title: a.activity_name || "Untitled",
    subtitle: meta,
    dateLabel,
    href: isTest
      ? `/tests/${encodeURIComponent(a.linked_test_id)}`
      : `/lesson/${encodeURIComponent(a.id)}`,
  };
};

// Sort by created_at desc, picking the latest N. Tolerates missing
// timestamps by treating them as oldest.
const recentN = (list, n = 3) =>
  [...(list || [])]
    .sort((a, b) => {
      const ta = a?.created_at ? Date.parse(a.created_at) : 0;
      const tb = b?.created_at ? Date.parse(b.created_at) : 0;
      return tb - ta;
    })
    .slice(0, n);

const CoverageBars = ({ title, data, total, testid }) => {
  const rows = sortDesc(data);
  // Phase 2 (Feb 2026 mid-month) — one denominator everywhere. Bar widths
  // now scale to `total` (activities in the reporting period), NOT to the
  // largest count in the card. Previously `(count / max) * 100` made the
  // top row visually 100% regardless of what fraction it represented,
  // which is the exact "3/11 looks like a full bar" bug.
  const denom = Math.max(1, Number(total) || 0);
  return (
    <div className="ae-card p-6 md:p-7" data-testid={testid}>
      <h3 className="font-extrabold text-lg text-[#4a6984] tracking-tight mb-5">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-[#4a6984]/60">No data yet.</p>
      ) : (
        <ul className="space-y-3">
          {rows.map(([name, count]) => (
            <li key={name}>
              <div className="flex items-center justify-between mb-1.5 gap-3">
                <span className="text-sm font-semibold text-[#4a6984] truncate">{name}</span>
                <span className="text-xs font-bold text-[#4a6984]/70">
                  {count}
                  {total ? ` / ${total}` : ""}
                </span>
              </div>
              <Progress
                value={(count / denom) * 100}
                className="h-2 bg-[#f4f5f7] [&>div]:bg-[#96ded1]"
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const LibraryCard = ({ activity, folders = [], onDelete, onActivityUpdate, onMove, onDragStart, onDragEnd, selectMode = false, selected = false, onToggleSelect, onScheduleUnit, onScheduleLesson }) => {
  // Phase 107 — `open` (lesson view modal) removed. The card "Open"
  // link now navigates to /lesson/:id (Lesson Workspace).
  const [current, setCurrent] = useState(activity);
  const [moving, setMoving] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  // Collapsed-by-default — teachers asked for a condensed library so
  // they can scan titles quickly. Per-card expansion is held in local
  // state only; we intentionally DO NOT persist it across refreshes
  // (each visit returns to a clean condensed view).
  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    setCurrent(activity);
  }, [activity]);
  const inputs = current.inputs || {};
  const currentFolder = folders.find((f) => f.id === current.folder_id) || null;
  const handleMove = async (folderId) => {
    if (folderId === current.folder_id) return;
    setMoving(true);
    try {
      await onMove(current.id, folderId);
    } finally {
      setMoving(false);
    }
  };
  const handleDragStart = (e) => {
    if (selectMode) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/atomicedu-activity-id", activity.id);
    setDragging(true);
    onDragStart?.(activity.id);
  };
  const handleDragEnd = () => {
    setDragging(false);
    onDragEnd?.();
  };
  const handleCardClick = (e) => {
    if (!selectMode) return;
    // In select mode, the whole card is a toggle. Only intercept when the
    // click target isn't an interactive child (button/input/anchor/dialog
    // trigger) — those still work normally so power users can preview
    // while selecting other cards.
    onToggleSelect?.(activity.id);
  };
  return (
    <div
      data-testid={`library-card-${activity.id}`}
      draggable={!selectMode}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={handleCardClick}
      className={`ae-card ${expanded ? "p-5 md:p-6" : "px-4 py-3"} hover:shadow-md transition-shadow ${
        selectMode
          ? selected
            ? "cursor-pointer ring-2 ring-[#96ded1] bg-[#96ded1]/10"
            : "cursor-pointer"
          : "cursor-grab active:cursor-grabbing"
      } ${
        dragging ? "opacity-40" : ""
      }`}
    >
      {selectMode && (
        <div
          className={`mb-3 inline-flex h-6 w-6 items-center justify-center rounded-md border-2 transition-colors ${
            selected
              ? "bg-[#96ded1] border-[#1c2d3d]"
              : "bg-white border-[#4a6984]/30"
          }`}
          data-testid={`library-checkbox-${activity.id}`}
          aria-checked={selected}
          role="checkbox"
        >
          {selected && <Check className="h-4 w-4 text-[#1c2d3d]" />}
        </div>
      )}

      {/* Title bar — always rendered. Click to expand / collapse the
          full lesson card. Stops propagation so clicking the title
          bar in select mode doesn't toggle the checkbox. */}
      <button
        type="button"
        onClick={(e) => {
          if (selectMode) return; // let the card's outer click handler win
          e.stopPropagation();
          setExpanded((v) => !v);
        }}
        data-testid={`library-card-toggle-${activity.id}`}
        aria-expanded={expanded}
        aria-label={expanded ? "Collapse lesson card" : "Expand lesson card"}
        className="w-full flex items-start justify-between gap-3 text-left"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-bold uppercase text-[10px] tracking-[0.2em] text-[#4a6984]/60">
              {fmt(activity.created_at)}
            </p>
            {(activity.output_type === "unit" || activity.unit_markdown) && (
              <>
                <span
                  data-testid="library-unit-badge"
                  className="font-bold uppercase text-[10px] tracking-[0.18em] px-2 py-0.5 rounded-full bg-[#96ded1]/30 text-[#1c2d3d] border border-[#96ded1]/60"
                >
                  Unit{activity.lesson_count ? ` · ${activity.lesson_count}` : ""}
                </span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onScheduleUnit && onScheduleUnit(activity); }}
                  data-testid={`library-schedule-unit-pill-${activity.id}`}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full bg-white text-[#1c2d3d] border border-[#1c2d3d]/30 hover:bg-[#96ded1]/15"
                  title="Place every lesson in this unit on your Planner"
                >
                  <CalendarDays className="h-3 w-3" /> Schedule
                </button>
              </>
            )}
            {activity.output_type === "summative_task" && (
              <>
                <span
                  data-testid="library-summative-badge"
                  className="font-bold uppercase text-[10px] tracking-[0.18em] px-2 py-0.5 rounded-full bg-[#1c2d3d] text-[#96ded1] border border-[#96ded1]/60"
                >
                  Summative{activity.lesson_count ? ` · ${activity.lesson_count}` : ""}
                </span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onScheduleUnit && onScheduleUnit(activity); }}
                  data-testid={`library-schedule-summative-pill-${activity.id}`}
                  className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full bg-white text-[#1c2d3d] border border-[#1c2d3d]/30 hover:bg-[#96ded1]/15"
                  title="Block out class periods on your Planner for this summative task"
                >
                  <CalendarDays className="h-3 w-3" /> Schedule
                </button>
              </>
            )}
            {activity.output_type === "supply_plan" && (
              <span
                data-testid="library-supply-badge"
                className="font-bold uppercase text-[10px] tracking-[0.18em] px-2 py-0.5 rounded-full bg-black text-white"
              >
                Supply
              </span>
            )}
            {/* Phase 120 — Library save-bug fix: Test stub badge */}
            {activity.output_type === "test" && (
              <span
                data-testid={`library-test-badge-${activity.id}`}
                className="font-bold uppercase text-[10px] tracking-[0.18em] px-2 py-0.5 rounded-full bg-[#f4843b] text-white border border-[#f4843b]"
                title="Test built in the Test Builder"
              >
                Test
              </span>
            )}
            {/* Phase 92 — community-sharing pills. */}
            {current.shared_to_community && (
              <span
                data-testid={`library-shared-badge-${activity.id}`}
                className="inline-flex items-center gap-1 font-bold uppercase text-[10px] tracking-[0.18em] px-2 py-0.5 rounded-full bg-[#96ded1]/35 text-[#1c2d3d] border border-[#96ded1]/70"
                title={
                  current.community_used_by_count
                    ? `Used by ${current.community_used_by_count} teacher${current.community_used_by_count === 1 ? "" : "s"}`
                    : "Shared with the Atomic Edu community"
                }
              >
                <Users className="h-3 w-3" /> Shared
                {current.community_used_by_count > 0 && (
                  <span className="ml-1 opacity-80">
                    · {current.community_used_by_count}
                  </span>
                )}
              </span>
            )}
            {current.imported_from_id && (
              <span
                data-testid={`library-from-community-badge-${activity.id}`}
                className="inline-flex items-center gap-1 font-bold uppercase text-[10px] tracking-[0.18em] px-2 py-0.5 rounded-full bg-white text-[#4a6984] border border-[#4a6984]/30"
                title="Imported from the Atomic Edu community"
              >
                <Users className="h-3 w-3" /> From Community
              </span>
            )}
            {activity.output_type === "math_activation" && (
              <span
                data-testid="library-math-engine-badge"
                className="font-bold uppercase text-[10px] tracking-[0.18em] px-2 py-0.5 rounded-full bg-[#1c2d3d] text-[#96ded1] border border-[#96ded1]/60"
              >
                Math Engine · Activation
              </span>
            )}
            {activity.output_type === "math_diagnostic" && (
              <span
                data-testid="library-math-engine-diagnostic-badge"
                className="font-bold uppercase text-[10px] tracking-[0.18em] px-2 py-0.5 rounded-full bg-[#1c2d3d] text-[#96ded1] border border-[#96ded1]/60"
              >
                Math Engine · Diagnostic
              </span>
            )}
            {activity.output_type === "math_lesson1" && (
              <span
                data-testid="library-math-engine-lesson1-badge"
                className="font-bold uppercase text-[10px] tracking-[0.18em] px-2 py-0.5 rounded-full bg-[#1c2d3d] text-[#96ded1] border border-[#96ded1]/60"
              >
                Math Engine · Lesson 1
              </span>
            )}
            {activity.output_type === "math_exit_review" && (
              <span
                data-testid="library-math-engine-exit-review-badge"
                className="font-bold uppercase text-[10px] tracking-[0.18em] px-2 py-0.5 rounded-full bg-[#1c2d3d] text-[#96ded1] border border-[#96ded1]/60"
              >
                Math Engine · Exit Ticket Review
              </span>
            )}
            {activity.output_type === "math_lesson2" && (
              <span
                data-testid="library-math-engine-lesson2-badge"
                className="font-bold uppercase text-[10px] tracking-[0.18em] px-2 py-0.5 rounded-full bg-[#1c2d3d] text-[#96ded1] border border-[#96ded1]/60"
              >
                Math Engine · Lesson 2
                {(activity.inputs?.math_engine?.branch_label) ? (
                  <span className="ml-1.5 opacity-80">
                    · {activity.inputs.math_engine.branch_label}
                  </span>
                ) : null}
              </span>
            )}
            {activity.output_type === "math_worksheet" && (
              <span
                data-testid="library-math-engine-worksheet-badge"
                className="font-bold uppercase text-[10px] tracking-[0.18em] px-2 py-0.5 rounded-full bg-[#1c2d3d] text-[#96ded1] border border-[#96ded1]/60"
              >
                Math Engine · Worksheet
                {activity.worksheet?.difficulty_label && activity.worksheet.difficulty_label !== "On Grade" ? (
                  <span className="ml-1.5 opacity-80">
                    · {activity.worksheet.difficulty_label}
                  </span>
                ) : null}
              </span>
            )}
            {activity.output_type === "math_task_extended" && (
              <span
                data-testid="library-math-engine-task-extended-badge"
                className="font-bold uppercase text-[10px] tracking-[0.18em] px-2 py-0.5 rounded-full bg-[#1c2d3d] text-[#96ded1] border border-[#96ded1]/60"
              >
                Math Engine · Extended Task
              </span>
            )}
            {activity.output_type === "math_task_lfhc" && (
              <span
                data-testid="library-math-engine-task-lfhc-badge"
                className="font-bold uppercase text-[10px] tracking-[0.18em] px-2 py-0.5 rounded-full bg-[#1c2d3d] text-[#96ded1] border border-[#96ded1]/60"
              >
                Math Engine · LFHC Task
              </span>
            )}
            {activity.output_type === "math_mid_unit_quiz" && (
              <span
                data-testid="library-math-engine-mid-quiz-badge"
                className="font-bold uppercase text-[10px] tracking-[0.18em] px-2 py-0.5 rounded-full bg-[#1c2d3d] text-[#96ded1] border border-[#96ded1]/60"
              >
                Math Engine · Mid-Unit Quiz
              </span>
            )}
            {activity.output_type === "math_unit_summary" && (
              <span
                data-testid="library-math-engine-unit-summary-badge"
                className="font-bold uppercase text-[10px] tracking-[0.18em] px-2 py-0.5 rounded-full bg-[#1c2d3d] text-[#96ded1] border border-[#96ded1]/60"
              >
                Math Engine · Unit Summary
              </span>
            )}
            {/* Phase 88 — quick-schedule pill that sits next to the math
                badge so teachers don't need to expand the card first. */}
            {activity.output_type?.startsWith("math_") && activity.output_type !== "math_unit_summary" && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onScheduleLesson && onScheduleLesson(activity); }}
                data-testid={`library-schedule-math-pill-${activity.id}`}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full bg-white text-[#1c2d3d] border border-[#1c2d3d]/30 hover:bg-[#96ded1]/15"
                title="Add this to your Planner"
              >
                <CalendarDays className="h-3 w-3" /> Schedule
              </button>
            )}
            {/* Phase 108.C — Tiny TAUGHT chip on the collapsed card so
                teachers can see at a glance which lessons fed Live
                Reports. Hidden when not taught to keep the header clean. */}
            {activity.taught ? (
              <span
                data-testid={`library-card-taught-pill-${activity.id}`}
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full bg-[#1c9e7e] text-white border border-[#1c9e7e]"
                title="You've confirmed teaching this lesson. Eligible for Live Reports."
              >
                <BookOpen className="h-3 w-3" /> Taught
              </span>
            ) : null}
          </div>
          <h4 className={`mt-1 font-extrabold ${expanded ? "text-lg" : "text-base"} text-[#4a6984] tracking-tight truncate`}>
            {activity.activity_name}
          </h4>
          {expanded && activity.tagline && (
            <p className="mt-1 text-sm text-[#4a6984]/80 line-clamp-2">{activity.tagline}</p>
          )}
        </div>
        <ChevronRight
          className={`h-4 w-4 text-[#4a6984]/55 shrink-0 mt-1 transition-transform ${
            expanded ? "rotate-90" : ""
          }`}
        />
      </button>

      {expanded && (
        <>
          <div className="mt-4 flex flex-wrap gap-2">
        {inputs.grade && <span className="ae-pill">{inputs.grade}</span>}
        {inputs.subject && <span className="ae-pill">{inputs.subject}</span>}
        {inputs.time && <span className="ae-pill">{inputs.time}</span>}
        {inputs.activity_type && <span className="ae-pill">{inputs.activity_type}</span>}
        {currentFolder && (
          <span
            data-testid={`library-card-folder-pill-${activity.id}`}
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-bold tracking-tight"
            style={{
              backgroundColor: `${currentFolder.color}33`,
              color: "#1c2d3d",
            }}
          >
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: currentFolder.color }}
            />
            {currentFolder.name}
          </span>
        )}
      </div>

      <div
        className="mt-5 flex flex-wrap items-center justify-between gap-3"
        data-testid={`library-card-actions-${activity.id}`}
      >
        <div className="flex flex-wrap gap-2">
          {/* Phase 120 — Test stubs swap the generic Open for
              test-specific Edit / Print / Mark actions. The test data
              lives in db.tests (not db.activities), so /lesson/:id
              would 404 — keep this branch tight and self-contained. */}
          {activity.output_type === "test" && activity.linked_test_id ? (
            <>
              <Link
                to={`/tests/${encodeURIComponent(activity.linked_test_id)}`}
                data-testid={`library-test-edit-${activity.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#96ded1] text-[#1c2d3d] font-extrabold text-xs uppercase tracking-[0.12em] hover:bg-[#96ded1]/85"
                title="Open the Test Builder to edit questions"
              >
                <Pencil className="h-3.5 w-3.5" /> Edit test
              </Link>
              <Link
                to={`/tests/${encodeURIComponent(activity.linked_test_id)}/mark`}
                data-testid={`library-test-mark-${activity.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white text-[#1c2d3d] font-extrabold text-xs uppercase tracking-[0.12em] border border-[#1c2d3d]/30 hover:bg-[#96ded1]/15"
                title="Mark student responses"
              >
                <ClipboardCheck className="h-3.5 w-3.5" /> Mark
              </Link>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/tests/${encodeURIComponent(activity.linked_test_id)}/student.pdf`}
                target="_blank"
                rel="noreferrer"
                data-testid={`library-test-print-student-${activity.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white text-[#1c2d3d] font-extrabold text-xs uppercase tracking-[0.12em] border border-[#1c2d3d]/30 hover:bg-[#96ded1]/15"
                title="Download the student copy PDF"
              >
                <FileText className="h-3.5 w-3.5" /> Student PDF
              </a>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/tests/${encodeURIComponent(activity.linked_test_id)}/teacher.pdf`}
                target="_blank"
                rel="noreferrer"
                data-testid={`library-test-print-teacher-${activity.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white text-[#1c2d3d] font-extrabold text-xs uppercase tracking-[0.12em] border border-[#1c2d3d]/30 hover:bg-[#96ded1]/15"
                title="Download the teacher copy with answer key"
              >
                <Printer className="h-3.5 w-3.5" /> Teacher PDF
              </a>
            </>
          ) : (
            <>
              {/* Phase 107 — Library cards slim down to a single primary
                  "Open" action. The View modal has been replaced by the
                  full-page Lesson Workspace at /lesson/:id which carries
                  every export, share, and refine surface. */}
              <Link
                to={`/lesson/${encodeURIComponent(activity.id)}`}
                data-testid={`library-open-${activity.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#96ded1] text-[#1c2d3d] font-extrabold text-xs uppercase tracking-[0.12em] hover:bg-[#96ded1]/85"
              >
                Open <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              {/* Phase 108.C — quick "Mark taught" toggle right next to
                  Open. Refresh the single row by re-fetching just this
                  activity to pick up the new `taught` / `taught_at`. */}
              <MarkTaughtButton
                activity={activity}
                size="sm"
                onChange={() => {
                  getActivity(activity.id)
                    .then((updated) => onActivityUpdate?.(updated))
                    .catch(() => {});
                }}
              />
            </>
          )}
          {/* Schedule pills retained — they're distinct destinations
              (Planner), not lesson-content surfaces. */}
          {(activity.output_type === "unit" || activity.unit_markdown) && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onScheduleUnit && onScheduleUnit(activity); }}
              data-testid={`library-schedule-unit-${activity.id}`}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1c2d3d] hover:bg-[#96ded1]/20 px-2 py-1 rounded-md border border-[#96ded1]/60"
              title="Place every lesson in this unit on your Planner"
            >
              <CalendarDays className="h-3.5 w-3.5" /> Schedule unit
            </button>
          )}
          {activity.output_type === "summative_task" && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onScheduleUnit && onScheduleUnit(activity); }}
              data-testid={`library-schedule-summative-${activity.id}`}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1c2d3d] hover:bg-[#96ded1]/20 px-2 py-1 rounded-md border border-[#96ded1]/60"
              title="Block out class periods on your Planner for this summative task"
            >
              <CalendarDays className="h-3.5 w-3.5" /> Schedule task
            </button>
          )}
          {activity.output_type?.startsWith("math_") && activity.output_type !== "math_unit_summary" && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onScheduleLesson && onScheduleLesson(activity); }}
              data-testid={`library-schedule-math-${activity.id}`}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-[#1c2d3d] hover:bg-[#96ded1]/20 px-2 py-1 rounded-md border border-[#96ded1]/60"
              title="Add this to your Planner"
            >
              <CalendarDays className="h-3.5 w-3.5" /> Schedule
            </button>
          )}
          {activity.output_type?.startsWith("math_") && (
            <Link
              to="/math-engine"
              data-testid={`library-math-continue-${activity.id}`}
              className="inline-flex items-center gap-1.5 text-xs font-bold text-[#4a6984] hover:text-[#1c2d3d] px-2 py-1 rounded-md hover:bg-[#96ded1]/20"
              title="Open the Math Engine — your in-flight workflow will surface in the Resume panel"
            >
              Continue in Math Engine →
            </Link>
          )}
        </div>
        {/* ⋯ Overflow menu — Move to…, Delete, Share. Phase 130 —
            bumped touch target to h-9 w-9 with a visible border so the
            entry point is obvious on mobile. Without this teachers
            were missing the Delete option entirely. */}
        <div className="flex items-center gap-1">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                data-testid={`library-overflow-${activity.id}`}
                aria-label="More actions including delete"
                title="More actions — move, share, delete"
                className="text-[#4a6984] hover:text-[#1c2d3d] hover:bg-[#f4f5f7] h-9 w-9 p-0 border border-[#4a6984]/15 hover:border-[#4a6984]/35 rounded-md"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-white border-[#4a6984]/15 min-w-[260px]"
              data-testid={`library-overflow-menu-${activity.id}`}
            >
              <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-[#4a6984]/70">
                Move to…
              </DropdownMenuLabel>
              <DropdownMenuItem
                onSelect={() => handleMove(null)}
                data-testid={`library-move-unfiled-${activity.id}`}
                className="cursor-pointer"
              >
                <Inbox className="h-3.5 w-3.5 mr-2" /> Unfiled
                {!current.folder_id && (
                  <Check className="h-3.5 w-3.5 ml-auto text-[#4a6984]/60" />
                )}
              </DropdownMenuItem>
              {folders
                .filter(
                  (f) => !f.system || (current.output_type || "").startsWith("math_")
                )
                .map((f) => (
                <DropdownMenuItem
                  key={f.id}
                  onSelect={() => handleMove(f.id)}
                  data-testid={`library-move-target-${f.id}-${activity.id}`}
                  className="cursor-pointer"
                >
                  <span
                    className="h-2.5 w-2.5 rounded-sm mr-2 shrink-0"
                    style={{ backgroundColor: f.color }}
                  />
                  <span className="truncate">{f.name}</span>
                  {current.folder_id === f.id && (
                    <Check className="h-3.5 w-3.5 ml-auto text-[#4a6984]/60" />
                  )}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              {/* Share — single menu with two clear options */}
              {!current.imported_from_id && (
                <>
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-[#4a6984]/70">
                    Share
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    onSelect={async (e) => {
                      e.preventDefault();
                      try {
                        if (current.shared_to_community) {
                          await unshareActivity(activity.id);
                          const updated = { ...current, shared_to_community: false };
                          setCurrent(updated);
                          onActivityUpdate?.(updated);
                          toast.success("Removed from the community feed.");
                        } else {
                          const res = await shareActivity(activity.id, "");
                          const updated = {
                            ...current,
                            shared_to_community: true,
                            community_display_name: res.display_name,
                            shared_at: res.shared_at,
                          };
                          setCurrent(updated);
                          onActivityUpdate?.(updated);
                          toast.success("Shared with the Atomic Edu community.");
                        }
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Could not update share");
                      }
                    }}
                    data-testid={`library-share-community-${activity.id}`}
                    className="cursor-pointer flex-col items-start gap-1 py-2.5"
                  >
                    <span className="inline-flex items-center gap-2 font-bold text-sm">
                      <Users className="h-3.5 w-3.5" />
                      {current.shared_to_community
                        ? "Remove from community"
                        : "Share with the Atomic Edu community"}
                    </span>
                    <span className="text-[11px] text-[#4a6984]/75 leading-snug">
                      Teachers on a paid plan can preview and import a copy.
                      Your name, notes, and class details stay private.
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault();
                      setShareOpen(true);
                    }}
                    data-testid={`library-share-public-${activity.id}`}
                    className="cursor-pointer flex-col items-start gap-1 py-2.5"
                  >
                    <span className="inline-flex items-center gap-2 font-bold text-sm">
                      <Share2 className="h-3.5 w-3.5" /> Copy public link
                    </span>
                    <span className="text-[11px] text-[#4a6984]/75 leading-snug">
                      Anyone with the link can view this lesson.
                    </span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem
                onSelect={() => onDelete(activity)}
                data-testid={`library-delete-${activity.id}`}
                className="cursor-pointer text-red-600 focus:text-red-700 focus:bg-red-50"
              >
                <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
        </>
      )}
      <ShareLessonDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        activityId={activity.id}
        activityName={activity.activity_name}
        testid={`library-share-${activity.id}`}
      />
    </div>
  );
};

// Auto-group activities by inputs.subject. Within a folder, newest first.
// Folders themselves are sorted by their most-recent activity's date so the
// folder a teacher is actively working in always bubbles to the top.
// REMOVED in Phase 49 — replaced by teacher-managed custom folders.

const FOLDER_PALETTE = [
  "#96ded1", // mint
  "#a8d0e6", // sky
  "#f6c89f", // peach
  "#d8b4f8", // lavender
  "#bce5a3", // sage
  "#f5b7b1", // blush
];

// ============================================================
// Folder sidebar — Phase 49 / Phase 1 / Phase 81
// Top-to-bottom order: custom folders (manually orderable) → Unfiled
// → divider → All Lessons (pinned at the bottom).
// Up/down arrows reorder custom folders. Click a row to filter the
// main library list. "+ New Folder" opens a tiny inline form.
// ============================================================
const FolderSidebar = ({
  folders,
  activities,
  selectedFolderId,
  onSelect,
  onCreate,
  onDropActivity,
  isDragging,
  onUpdateFolder,
  onDeleteFolder,
  onReorderFolders,
}) => {
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState(FOLDER_PALETTE[0]);
  const [submitting, setSubmitting] = useState(false);

  const totalCount = activities.length;
  const unfiledCount = activities.filter((a) => !a.folder_id).length;
  const countFor = (fid) => activities.filter((a) => a.folder_id === fid).length;
  // Phase 122 — the platform-owned Math Engine folder. Its contents are
  // excluded from "All Lessons" so markable lessons and unmarkable Math
  // Engine cards stop mixing.
  const systemFolder = folders.find((f) => f.system);
  const systemFolderId = systemFolder?.id || null;
  const allLessonsCount = activities.filter(
    (a) => !systemFolderId || a.folder_id !== systemFolderId
  ).length;

  const handleSubmit = async () => {
    const name = newName.trim();
    if (!name) return;
    setSubmitting(true);
    try {
      await onCreate(name, newColor);
      setNewName("");
      setNewColor(FOLDER_PALETTE[0]);
      setCreating(false);
    } finally {
      setSubmitting(false);
    }
  };

  const Row = ({
    active,
    onClick,
    color,
    icon: Icon,
    label,
    count,
    testid,
    folderId,
    folder, // full folder object — only set for editable rows
    canMoveUp = false,
    canMoveDown = false,
    onMoveUp,
    onMoveDown,
  }) => {
    const [over, setOver] = useState(false);
    const [editing, setEditing] = useState(false);
    const [editName, setEditName] = useState(folder?.name || "");
    const [editColor, setEditColor] = useState(folder?.color || FOLDER_PALETTE[0]);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const droppable = folderId !== undefined; // null=Unfiled, string=folder, undefined=All Lessons (no drop)
    const isCustomFolder = !!folder;

    const handleDragOver = (e) => {
      if (!droppable) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (!over) setOver(true);
    };
    const handleDragLeave = () => {
      if (over) setOver(false);
    };
    const handleDrop = (e) => {
      if (!droppable) return;
      e.preventDefault();
      setOver(false);
      const activityId = e.dataTransfer.getData("application/atomicedu-activity-id");
      if (activityId && onDropActivity) {
        onDropActivity(activityId, folderId);
      }
    };
    const handleSaveEdit = async (e) => {
      e?.stopPropagation();
      const name = editName.trim();
      if (!name) return;
      const changes = {};
      if (name !== folder.name) changes.name = name;
      if (editColor !== folder.color) changes.color = editColor;
      if (Object.keys(changes).length === 0) {
        setEditing(false);
        return;
      }
      await onUpdateFolder(folder.id, changes);
      setEditing(false);
    };
    const handleConfirmDelete = async (e) => {
      e?.stopPropagation();
      await onDeleteFolder(folder.id);
      setConfirmDelete(false);
    };

    if (editing) {
      return (
        <div
          className="rounded-lg border border-[#4a6984]/20 p-3 space-y-2 bg-[#f4f5f7]/70"
          data-testid={`${testid}-edit`}
        >
          <input
            autoFocus
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSaveEdit(e);
              if (e.key === "Escape") setEditing(false);
            }}
            maxLength={80}
            data-testid={`${testid}-edit-name`}
            className="w-full px-2 py-1.5 text-sm rounded border border-[#4a6984]/20 focus:outline-none focus:border-[#4a6984]"
          />
          <div className="flex items-center gap-1.5">
            {FOLDER_PALETTE.map((c) => (
              <button
                key={c}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditColor(c);
                }}
                data-testid={`${testid}-edit-color-${c.replace("#", "")}`}
                className={`h-5 w-5 rounded-full border-2 transition-transform ${
                  editColor === c ? "scale-110 border-[#1c2d3d]" : "border-transparent"
                }`}
                style={{ backgroundColor: c }}
                aria-label={`Pick colour ${c}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              onClick={handleSaveEdit}
              disabled={!editName.trim()}
              data-testid={`${testid}-edit-save`}
              className="bg-[#96ded1] hover:bg-[#7dcfc0] text-[#1c2d3d] flex-1"
            >
              Save
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                setEditing(false);
                setEditName(folder.name);
                setEditColor(folder.color);
              }}
              data-testid={`${testid}-edit-cancel`}
              className="border-[#4a6984]/20"
            >
              Cancel
            </Button>
          </div>
        </div>
      );
    }

    if (confirmDelete) {
      return (
        <div
          className="rounded-lg border border-red-200 bg-red-50/60 p-3 space-y-2"
          data-testid={`${testid}-confirm-delete`}
        >
          <p className="text-xs font-bold text-[#1c2d3d]">
            Delete &ldquo;{folder.name}&rdquo;?
          </p>
          <p className="text-[11px] text-[#4a6984]/80 leading-snug">
            The {count} {count === 1 ? "lesson" : "lessons"} inside will move to
            Unfiled. Nothing is deleted.
          </p>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              onClick={handleConfirmDelete}
              data-testid={`${testid}-confirm-delete-yes`}
              className="bg-red-600 hover:bg-red-700 text-white flex-1"
            >
              Delete folder
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                setConfirmDelete(false);
              }}
              data-testid={`${testid}-confirm-delete-no`}
              className="border-[#4a6984]/20"
            >
              Cancel
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div
        className={`group flex items-stretch rounded-lg overflow-hidden transition-colors ${
          active
            ? "bg-[#4a6984]/10"
            : "hover:bg-[#f4f5f7]"
        } ${
          over && droppable
            ? "ring-2 ring-[#96ded1] ring-offset-1 bg-[#96ded1]/15"
            : isDragging && droppable
              ? "outline outline-2 outline-dashed outline-[#96ded1]/40"
              : ""
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <button
          type="button"
          onClick={onClick}
          data-testid={testid}
          className={`flex-1 min-w-0 flex items-center gap-3 px-3 py-2 text-left ${
            active ? "text-[#1c2d3d]" : "text-[#4a6984]"
          }`}
        >
          {color ? (
            <span
              className="h-3 w-3 rounded-sm shrink-0"
              style={{ backgroundColor: color }}
            />
          ) : (
            <Icon className="h-4 w-4 shrink-0" />
          )}
          <span className="text-sm font-semibold truncate flex-1">{label}</span>
          <span className="text-[11px] font-bold text-[#4a6984]/60 tabular-nums">
            {count}
          </span>
        </button>
        {isCustomFolder && (
          <div className="flex items-stretch opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onMoveUp && onMoveUp();
              }}
              disabled={!canMoveUp}
              data-testid={`${testid}-up`}
              aria-label={`Move ${label} up`}
              title="Move up"
              className="px-1.5 text-[#4a6984]/55 hover:text-[#1c2d3d] disabled:opacity-30 disabled:hover:text-[#4a6984]/55 disabled:cursor-not-allowed"
            >
              <ChevronUp className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onMoveDown && onMoveDown();
              }}
              disabled={!canMoveDown}
              data-testid={`${testid}-down`}
              aria-label={`Move ${label} down`}
              title="Move down"
              className="px-1.5 text-[#4a6984]/55 hover:text-[#1c2d3d] disabled:opacity-30 disabled:hover:text-[#4a6984]/55 disabled:cursor-not-allowed"
            >
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
        {isCustomFolder && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                data-testid={`${testid}-menu`}
                onClick={(e) => e.stopPropagation()}
                className="px-2 text-[#4a6984]/50 hover:text-[#1c2d3d] opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                aria-label={`Folder options for ${label}`}
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-white border-[#4a6984]/15 min-w-[170px]"
            >
              <DropdownMenuItem
                onSelect={() => {
                  setEditName(folder.name);
                  setEditColor(folder.color);
                  setEditing(true);
                }}
                data-testid={`${testid}-menu-rename`}
                className="cursor-pointer"
              >
                <Pencil className="h-3.5 w-3.5 mr-2" /> Rename
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => {
                  setEditName(folder.name);
                  setEditColor(folder.color);
                  setEditing(true);
                }}
                data-testid={`${testid}-menu-color`}
                className="cursor-pointer"
              >
                <Palette className="h-3.5 w-3.5 mr-2" /> Change colour
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() => {
                  // Phase 123 — for folders that have any contents (or
                  // child folders), skip the inline "are you sure?" and
                  // open the cascade-handling dialog directly so the
                  // teacher only sees one confirm step.
                  const hasChildren = count > 0;
                  if (hasChildren) {
                    onDeleteFolder?.(folder.id);
                  } else {
                    setConfirmDelete(true);
                  }
                }}
                data-testid={`${testid}-menu-delete`}
                className="cursor-pointer text-red-600 focus:text-red-700"
              >
                <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete folder
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    );
  };

  return (
    <aside
      className="ae-card p-3 md:p-4 self-start lg:sticky lg:top-24"
      data-testid="library-sidebar"
    >
      {!creating ? (
        <Button
          type="button"
          size="sm"
          onClick={() => setCreating(true)}
          data-testid="folder-new-btn"
          className="w-full bg-[#4a6984] hover:bg-[#1c2d3d] text-white"
        >
          <Plus className="h-4 w-4 mr-1.5" /> New Folder
        </Button>
      ) : (
        <div
          className="rounded-lg border border-[#4a6984]/15 p-3 space-y-2"
          data-testid="folder-new-form"
        >
          <input
            autoFocus
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSubmit();
              if (e.key === "Escape") setCreating(false);
            }}
            placeholder="Folder name"
            maxLength={80}
            data-testid="folder-new-name"
            className="w-full px-2 py-1.5 text-sm rounded border border-[#4a6984]/20 focus:outline-none focus:border-[#4a6984]"
          />
          <div className="flex items-center gap-1.5">
            {FOLDER_PALETTE.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setNewColor(c)}
                data-testid={`folder-new-color-${c.replace("#", "")}`}
                className={`h-5 w-5 rounded-full border-2 transition-transform ${
                  newColor === c ? "scale-110 border-[#1c2d3d]" : "border-transparent"
                }`}
                style={{ backgroundColor: c }}
                aria-label={`Pick colour ${c}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              onClick={handleSubmit}
              disabled={!newName.trim() || submitting}
              data-testid="folder-new-save"
              className="bg-[#96ded1] hover:bg-[#7dcfc0] text-[#1c2d3d] flex-1"
            >
              {submitting ? "Saving…" : "Create"}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => {
                setCreating(false);
                setNewName("");
              }}
              data-testid="folder-new-cancel"
              className="border-[#4a6984]/20"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="mt-4 space-y-0.5">
        {/* Phase 121 — Subject -> Unit hierarchy.
            Subject folders (parent_folder_id=null) render at the top
            level. Their child Unit folders nest indented underneath. */}
        {(() => {
          const subjects = folders.filter((f) => !f.parent_folder_id && !f.system);
          const unitsBySubject = new Map();
          folders.forEach((f) => {
            if (f.parent_folder_id) {
              const list = unitsBySubject.get(f.parent_folder_id) || [];
              list.push(f);
              unitsBySubject.set(f.parent_folder_id, list);
            }
          });
          return subjects.map((f, i) => {
            const children = unitsBySubject.get(f.id) || [];
            const childCount = children.reduce(
              (sum, c) => sum + countFor(c.id),
              0
            );
            return (
              <div key={f.id} data-testid={`folder-subject-group-${f.id}`}>
                <Row
                  active={selectedFolderId === f.id}
                  onClick={() => onSelect(f.id)}
                  color={f.color || FOLDER_PALETTE[0]}
                  label={f.name}
                  // Include child-folder counts so the Subject row
                  // reflects every item in the tree.
                  count={countFor(f.id) + childCount}
                  testid={`folder-row-${f.id}`}
                  folderId={f.id}
                  folder={f}
                  canMoveUp={i > 0}
                  canMoveDown={i < subjects.length - 1}
                  onMoveUp={() => onReorderFolders && onReorderFolders(f.id, "up")}
                  onMoveDown={() => onReorderFolders && onReorderFolders(f.id, "down")}
                />
                {children.map((c) => (
                  <div
                    key={c.id}
                    data-testid={`folder-unit-wrap-${c.id}`}
                    className="ml-5 border-l-2 border-[#4a6984]/15 pl-2"
                  >
                    <Row
                      active={selectedFolderId === c.id}
                      onClick={() => onSelect(c.id)}
                      color={c.color || FOLDER_PALETTE[1] || FOLDER_PALETTE[0]}
                      label={c.name}
                      count={countFor(c.id)}
                      testid={`folder-row-${c.id}`}
                      folderId={c.id}
                      folder={c}
                    />
                  </div>
                ))}
              </div>
            );
          });
        })()}
        {folders.length > 0 && (
          <div className="my-2 border-t border-[#4a6984]/10" />
        )}
        {/* Phase 122 — platform-owned Math Engine folder, pinned and
            visually distinct from teacher folders. Not editable (no
            `folder` prop → no rename/delete UI); droppable so the
            backend can reject non-Math-Engine items. */}
        {systemFolder && (
          <Row
            active={selectedFolderId === systemFolderId}
            onClick={() => onSelect(systemFolderId)}
            icon={Calculator}
            color={systemFolder.color}
            label={systemFolder.name}
            count={countFor(systemFolderId)}
            testid="folder-row-math-engine"
            folderId={systemFolderId}
          />
        )}
        {/* Unfiled — the DEFAULT landing folder. */}
        <Row
          active={selectedFolderId === "__unfiled__"}
          onClick={() => onSelect("__unfiled__")}
          icon={Inbox}
          label="Unfiled"
          count={unfiledCount}
          testid="folder-row-unfiled"
          folderId={null}
        />
        {/* All Lessons pinned at the BOTTOM — under its own divider.
            Phase 122 — count/contents EXCLUDE the Math Engine folder. */}
        <div className="my-2 border-t border-[#4a6984]/10" />
        <Row
          active={selectedFolderId === null}
          onClick={() => onSelect(null)}
          icon={Library}
          label="All Lessons"
          count={allLessonsCount}
          testid="folder-row-all"
        />
      </div>
    </aside>
  );
};

export const Dashboard = () => {
  // Lane 4 (Feb 2026) — Walled-garden home for signed-in FREE users.
  // A thin router hooks the auth context ONCE at the top (satisfies
  // React's rules-of-hooks) and swaps between <FreeHome /> and the
  // paid Dashboard body. Paid users hit the exact same component
  // tree they've always used.
  const { user, loading: authLoading } = useAuth();
  if (!authLoading && user && !isPaidUser(user)) {
    return <FreeHome />;
  }
  return <PaidDashboardBody />;
};

const PaidDashboardBody = () => {
  // Basic-tier report-card nudge lane (Feb 2026) — signal ONLY, does
  // not change any other paid behaviour. Standard / Pro / Pro+ users
  // never see the banner because `isBasicUser` matches `tier_id ===
  // "basic"` strictly.
  const { user: _rc_nudge_user } = useAuth();
  const _rc_nudge_show_basic = isBasicUser(_rc_nudge_user);
  // Lane 2 (Feb 09 2026) — Home restructure needs the same user
  // object for the Today card's Pro-gate. Alias to avoid renaming the
  // long-standing `_rc_nudge_user` name and its callsites.
  const user = _rc_nudge_user;
  const [coverage, setCoverage] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  // Active tab — controlled so the Tracker can deep-link teachers into
  // the Library filtered to a single skill / expectation, and so
  // external links (e.g. the Skill in the Wild widget) can deep-link
  // via the `?tab=coverage` URL param.
  // Phase 115.1 — Dashboard becomes the Home surface by default. The
  // old internal tab row is gone; each rail entry now writes its own
  // `?view=` query param so each destination has a clean URL. We
  // honor the legacy `?tab=` param for one release so any external
  // bookmarks redirect correctly.
  const [tab, setTab] = useState(() => {
    if (typeof window === "undefined") return "home";
    const sp = new URLSearchParams(window.location.search);
    const requested = sp.get("view") || sp.get("tab");
    const allowed = new Set([
      "home",
      "library",
      "planner",
      "report-cards",
      "community",
      // Coverage Tracker is its own destination again as of Phase
      // 115.8 — the `#coverage` anchor on the LRP canvas pointed at
      // the entire LRP grid, which made "Coverage Tracker" feel like
      // it just opened the Long Range Plan with no change.
      "tracker",
      // Legacy alias — old `?view=coverage` bookmarks land on the
      // tracker so they continue to resolve.
      "coverage",
    ]);
    if (requested && allowed.has(requested)) {
      if (requested === "coverage") return "tracker";
      return requested;
    }
    return "home";
  });
  // Phase 115.6 — keep the active view in sync with the URL when the
  // teacher navigates from the LeftRail (e.g. /dashboard?view=library
  // → /dashboard?view=planner). Without this effect the URL changes
  // but the rendered view stays stuck on the value set at mount, so
  // rail items pointing at /dashboard?view=… appear broken.
  const _routerLocation = useLocation();
  useEffect(() => {
    const sp = new URLSearchParams(_routerLocation.search);
    const requested = sp.get("view") || sp.get("tab");
    const allowed = new Set([
      "home", "library", "planner", "report-cards", "community", "tracker", "coverage",
    ]);
    let next = "home";
    if (requested && allowed.has(requested)) {
      next = requested === "coverage" ? "tracker" : requested;
    }
    setTab((prev) => (prev === next ? prev : next));
  }, [_routerLocation.search]);
  // {label, activityIds:[]} | null
  const [libraryFilter, setLibraryFilter] = useState(null);
  // Phase 152.AA — segmented control on the Library tab. Persisted to
  // the URL as `?section=lessons|summative|support|isps` so deep-links
  // (e.g. a Notification linking to a teacher's ISPs) land on the
  // right section. Default = lessons (most-used).
  const [librarySection, setLibrarySection] = useState(() => {
    if (typeof window === "undefined") return "lessons";
    const sp = new URLSearchParams(window.location.search);
    const v = sp.get("section");
    return ["lessons", "summative", "support", "isps", "supply"].includes(v) ? v : "lessons";
  });
  useEffect(() => {
    const sp = new URLSearchParams(_routerLocation.search);
    const v = sp.get("section");
    const next = ["lessons", "summative", "support", "isps", "supply"].includes(v) ? v : "lessons";
    setLibrarySection((prev) => (prev === next ? prev : next));
  }, [_routerLocation.search]);
  const _navigate = useNavigate();
  const changeLibrarySection = (next) => {
    setLibrarySection(next);
    if (typeof window === "undefined") return;
    const sp = new URLSearchParams(window.location.search);
    sp.set("view", "library");
    if (next === "lessons") sp.delete("section");
    else sp.set("section", next);
    _navigate(`/dashboard?${sp.toString()}`, { replace: true });
  };
  // Phase 123 — Library search. Matches against activity name, grade,
  // subject, strand, and (when relevant) the linked test's title.
  const [searchQuery, setSearchQuery] = useState("");
  // Phase 123 — Cascade-delete dialog state. Opened when the teacher
  // clicks Delete on a folder that has activities or child folders;
  // empty folders bypass the dialog and delete immediately.
  const [cascadeDeleteFolder, setCascadeDeleteFolder] = useState(null);
  // Feb 2026 — "Readings only" chip filters the visible list to
  // lessons that have a generated Reading + Response block attached.
  // Applied on TOP of folder selection and the Tracker filter so it
  // composes naturally with the existing controls.
  const [readingsOnly, setReadingsOnly] = useState(false);
  // Phase 49 — custom folders.
  // folders: server list. selectedFolderId: null = All Lessons,
  // "__unfiled__" = Unfiled, "__browse__" = Subject-tile browse landing
  // (Phase 124.E default), otherwise a folder.id.
  const [folders, setFolders] = useState([]);
  // Phase 124.E — the default landing is the new Subject-tile browse
  // view so teachers see their library file-browser-style (Subjects →
  // Units → contents) instead of an "Unfiled" item dump.
  const [selectedFolderId, setSelectedFolderId] = useState("__browse__");
  // Phase 124.E — collapsible "Folders" sidebar. Default collapsed so
  // the tile view gets the full width; teachers who want the tree can
  // toggle it open.
  const [folderSidebarOpen, setFolderSidebarOpen] = useState(false);
  // Phase 83b — modal state for unit scheduling.
  const [schedulingUnit, setSchedulingUnit] = useState(null);
  // Phase 88 — single-period scheduling for any non-unit activity (Math
  // Engine lessons, supply plans, lesson activities, …). Mounting
  // <PostGenScheduleDialog> with `key={activity.id}` re-uses the
  // post-generation popup as a manual scheduler.
  const [schedulingLesson, setSchedulingLesson] = useState(null);
  // Drag-and-drop UX state (Phase 3) — true while a card is mid-drag so
  // the sidebar can show dashed drop-zone outlines.
  const [draggingActivityId, setDraggingActivityId] = useState(null);
  // Bulk-select state (Phase 5). When `selectMode` is true the cards
  // render with checkboxes and clicks toggle membership in `selectedIds`.
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // Bulk-select of folders (Subjects + Units) — coexists with the
  // activity selection above so a teacher can grab folders and loose
  // lessons in one sweep and move/delete them together.
  const [selectedFolderIds, setSelectedFolderIds] = useState(() => new Set());
  // Batch folder cascade-delete payload: { folderIds, extraActivityIds,
  // activityCount, childFolderCount } | null.
  const [batchCascade, setBatchCascade] = useState(null);
  // Phase 130 — Reliable delete confirmation dialog state. Replaces
  // `window.confirm()` which is silently blocked on some mobile
  // WebViews. `pendingDelete` is either null, a single activity
  // object (single-card delete), or { bulk: true, ids: [...] }.
  const [pendingDelete, setPendingDelete] = useState(null);
  // {learning_skill, nonce} | null — prefill payload handed to the
  // Report Cards tab when the teacher clicks "Draft a comment" on a
  // Tracker Learning Skill chip. `nonce` lets the same skill re-trigger
  // a prefill if clicked twice in a row.
  const [reportCardPrefill, setReportCardPrefill] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [c, a, f] = await Promise.all([
        getCoverage(),
        listActivities(),
        listFolders(),
      ]);
      setCoverage(c);
      setActivities(a);
      setFolders(f);
    } catch (e) {
      toast.error("Could not load dashboard", { description: e?.message });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFolder = async (name, color) => {
    try {
      const created = await createFolder(name, color);
      setFolders((list) => [...list, created]);
      setSelectedFolderId(created.id);
      toast.success(`Folder "${name}" created`);
    } catch (e) {
      toast.error("Could not create folder", { description: e?.message });
    }
  };

  // Phase 124.E — quick-add Unit folder nested under a Subject. Used by
  // the tile-browse "+ Unit" buttons so teachers can spin up Units
  // without opening the disclosure tree.
  const handleCreateUnitUnderSubject = async (subjectId, name, color) => {
    try {
      const created = await createFolder(name, color, {
        kind: "unit",
        parentFolderId: subjectId,
      });
      setFolders((list) => [...list, created]);
      toast.success(`Unit "${name}" created`);
    } catch (e) {
      toast.error("Could not create unit", { description: e?.message });
    }
  };

  const handleMove = async (activityId, folderId) => {
    try {
      await moveActivityToFolder(activityId, folderId);
      setActivities((list) =>
        list.map((a) => (a.id === activityId ? { ...a, folder_id: folderId } : a))
      );
      const target =
        folderId === null
          ? "Unfiled"
          : folders.find((f) => f.id === folderId)?.name || "folder";
      toast.success(`Moved to ${target}`);
    } catch (e) {
      toast.error("Could not move lesson", { description: e?.message });
    }
  };

  const handleUpdateFolder = async (folderId, changes) => {
    try {
      const updated = await updateFolder(folderId, changes);
      setFolders((list) => list.map((f) => (f.id === folderId ? updated : f)));
      toast.success("Folder updated");
    } catch (e) {
      toast.error("Could not update folder", { description: e?.message });
    }
  };

  const handleDeleteFolder = async (folderId) => {
    // Phase 123 — if the folder has any contents, open the cascade
    // dialog so the teacher explicitly picks Unfile / Reparent /
    // Delete all. Empty folders bypass the dialog.
    const folder = folders.find((f) => f.id === folderId);
    if (!folder) return;
    const activityCount = activities.filter(
      (a) => a.folder_id === folderId
    ).length;
    const childFolderCount = folders.filter(
      (f) => f.parent_folder_id === folderId
    ).length;
    if (activityCount === 0 && childFolderCount === 0) {
      await _performCascadeDelete(folderId, "unfile");
      return;
    }
    setCascadeDeleteFolder({
      folder,
      activityCount,
      childFolderCount,
    });
  };

  const _performCascadeDelete = async (folderId, cascade) => {
    try {
      const result = await deleteFolderWithCascade(folderId, cascade);
      // Reflect deletion locally. For cascade='delete_all' we strip
      // both the folder and every activity in the tree; for the other
      // modes we just unfile activities back to None.
      const childIds = folders
        .filter((f) => f.parent_folder_id === folderId)
        .map((f) => f.id);
      const all = [folderId, ...childIds];
      setFolders((list) => {
        if (cascade === "reparent") {
          // Lift child Unit folders to top-level Subjects in local state
          // so the sidebar updates without a refetch.
          return list
            .filter((f) => f.id !== folderId)
            .map((f) =>
              f.parent_folder_id === folderId
                ? { ...f, parent_folder_id: null, kind: "subject" }
                : f
            );
        }
        // unfile + delete_all both remove this folder + child folders.
        return list.filter((f) => !all.includes(f.id));
      });
      setActivities((list) => {
        if (cascade === "delete_all") {
          return list.filter((a) => !all.includes(a.folder_id));
        }
        // unfile and reparent both push activities (or descendant
        // activities for unfile) to folder_id=null.
        return list.map((a) =>
          all.includes(a.folder_id) ? { ...a, folder_id: null } : a
        );
      });
      if (selectedFolderId === folderId || childIds.includes(selectedFolderId)) {
        setSelectedFolderId(null);
      }
      const affected = result?.activities_affected ?? 0;
      const childHandled = result?.child_folders_handled ?? 0;
      if (cascade === "delete_all") {
        toast.success(
          affected > 0
            ? `Folder deleted · ${affected} activit${affected === 1 ? "y" : "ies"} removed`
            : "Folder deleted"
        );
      } else if (cascade === "reparent") {
        toast.success(
          childHandled > 0
            ? `Folder deleted · ${childHandled} child folder${childHandled === 1 ? "" : "s"} promoted to top level`
            : "Folder deleted"
        );
      } else {
        toast.success(
          affected > 0
            ? `Folder deleted · ${affected} activit${affected === 1 ? "y" : "ies"} moved to Unfiled`
            : "Folder deleted"
        );
      }
    } catch (e) {
      toast.error("Could not delete folder", { description: e?.message });
    } finally {
      setCascadeDeleteFolder(null);
    }
  };

  // Move a folder one slot up/down in the sidebar. We compute the new
  // order locally, optimistically render, then call the server. If the
  // server rejects we fall back to the previous order with a toast.
  const handleReorderFolders = async (folderId, direction) => {
    const prev = folders;
    const idx = prev.findIndex((f) => f.id === folderId);
    if (idx < 0) return;
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= prev.length) return;
    const next = [...prev];
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    setFolders(next);
    try {
      const result = await reorderFolders(next.map((f) => f.id));
      if (Array.isArray(result?.folders)) setFolders(result.folders);
    } catch (e) {
      setFolders(prev);
      toast.error("Couldn't save folder order", { description: e?.message });
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectFolder = (id) => {
    setSelectedFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
    setSelectedFolderIds(new Set());
  };

  // Select-all — context aware. In a folder/list view it grabs every
  // visible lesson; on the tile landing it grabs every non-system
  // Subject folder. Called with the list of activity ids currently in
  // view so it works for both.
  const selectAllActivities = (ids) => setSelectedIds(new Set(ids));
  const selectAllFolders = (ids) => setSelectedFolderIds(new Set(ids));

  const bulkMoveActivities = async (ids, folderId) => {
    if (!ids.length) return;
    try {
      await Promise.all(ids.map((id) => moveActivityToFolder(id, folderId)));
      const idSet = new Set(ids);
      setActivities((list) =>
        list.map((a) => (idSet.has(a.id) ? { ...a, folder_id: folderId } : a))
      );
      const target =
        folderId === null
          ? "Unfiled"
          : folders.find((f) => f.id === folderId)?.name || "folder";
      toast.success(`Moved ${ids.length} lesson${ids.length === 1 ? "" : "s"} to ${target}`);
      exitSelectMode();
    } catch (e) {
      toast.error("Some lessons could not be moved", { description: e?.message });
      reload();
    }
  };

  const handleBulkMove = (folderId) => bulkMoveActivities([...selectedIds], folderId);

  // Move selected UNIT folders under a target Subject folder.
  const bulkMoveUnits = async (unitIds, targetSubjectId) => {
    if (!unitIds.length || !targetSubjectId) return;
    try {
      await Promise.all(unitIds.map((id) => moveFolder(id, targetSubjectId)));
      const idSet = new Set(unitIds);
      setFolders((list) =>
        list.map((f) =>
          idSet.has(f.id)
            ? { ...f, parent_folder_id: targetSubjectId, kind: "unit" }
            : f
        )
      );
      const target = folders.find((f) => f.id === targetSubjectId)?.name || "subject";
      toast.success(`Moved ${unitIds.length} unit${unitIds.length === 1 ? "" : "s"} to ${target}`);
      exitSelectMode();
    } catch (e) {
      toast.error("Some units could not be moved", { description: e?.message });
      reload();
    }
  };

  // Unified bulk delete. If any folders are selected we route through
  // the batch cascade dialog (teacher picks unfile / reparent /
  // delete-all); otherwise the simpler bulk-lesson confirm.
  const handleBulkDelete = () => {
    const activityIds = [...selectedIds];
    const folderIds = [...selectedFolderIds].filter((id) => {
      const f = folders.find((x) => x.id === id);
      return f && !f.system;
    });
    if (!activityIds.length && !folderIds.length) return;
    if (folderIds.length === 0) {
      setPendingDelete({ bulk: true, ids: activityIds });
      return;
    }
    // Aggregate contents across the selected folder subtrees.
    const childFolderIds = folders
      .filter((f) => folderIds.includes(f.parent_folder_id))
      .map((f) => f.id);
    const treeIds = new Set([...folderIds, ...childFolderIds]);
    const activityCount = activities.filter(
      (a) => treeIds.has(a.folder_id) && !selectedIds.has(a.id)
    ).length;
    const childFolderCount = childFolderIds.length;
    if (activityCount === 0 && childFolderCount === 0 && activityIds.length === 0) {
      // Nothing inside — delete the empty folders straight away.
      performBatchCascade("unfile", { folderIds, extraActivityIds: [] });
      return;
    }
    setBatchCascade({
      folderIds,
      extraActivityIds: activityIds,
      activityCount,
      childFolderCount,
    });
  };

  const performBatchCascade = async (mode, override = null) => {
    const src = override || batchCascade;
    if (!src) return;
    const { folderIds, extraActivityIds } = src;
    try {
      await Promise.all(
        (folderIds || []).map((id) => deleteFolderWithCascade(id, mode))
      );
      await Promise.all(
        (extraActivityIds || []).map((id) => deleteActivity(id))
      );
      const n = (folderIds || []).length;
      toast.success(
        `Deleted ${n} folder${n === 1 ? "" : "s"}` +
          (extraActivityIds?.length
            ? ` · ${extraActivityIds.length} lesson${extraActivityIds.length === 1 ? "" : "s"}`
            : "")
      );
      setBatchCascade(null);
      exitSelectMode();
      reload();
    } catch (e) {
      toast.error("Some folders could not be deleted", { description: e?.message });
      setBatchCascade(null);
      reload();
    }
  };

  const performBulkDelete = async (ids) => {
    try {
      await Promise.all(ids.map((id) => deleteActivity(id)));
      const idSet = new Set(ids);
      setActivities((list) => list.filter((a) => !idSet.has(a.id)));
      toast.success(`Deleted ${ids.length} lesson${ids.length === 1 ? "" : "s"}`);
      exitSelectMode();
    } catch (e) {
      toast.error("Some lessons could not be deleted", { description: e?.message });
      reload();
    }
  };

  useEffect(() => { reload(); }, []);

  // Shared bulk-action bar for both the tile landing and the in-folder
  // list. Adapts its Move/Delete affordances to whatever mix of loose
  // lessons + folders is currently selected.
  const renderBulkBar = ({ selectableActivityIds = [], selectableFolderIds = [] } = {}) => {
    const selFolders = [...selectedFolderIds]
      .map((id) => folders.find((f) => f.id === id))
      .filter((f) => f && !f.system);
    const selUnits = selFolders.filter((f) => f.parent_folder_id);
    const selSubjects = selFolders.filter((f) => !f.parent_folder_id);
    const actCount = selectedIds.size;
    const folderCount = selFolders.length;
    const total = actCount + folderCount;

    let moveKind = null;
    if (folderCount > 0 && actCount === 0 && selSubjects.length === 0 && selUnits.length > 0) {
      moveKind = "units";
    } else if (folderCount === 0 && actCount > 0) {
      moveKind = "activities";
    }
    const subjectTargets = folders.filter((f) => !f.parent_folder_id && !f.system);
    const activityTargets = folders.filter((f) => !f.system);

    const parts = [];
    if (folderCount) parts.push(`${folderCount} folder${folderCount === 1 ? "" : "s"}`);
    if (actCount) parts.push(`${actCount} lesson${actCount === 1 ? "" : "s"}`);
    const countLabel = parts.length ? `${parts.join(" · ")} selected` : "Nothing selected";

    const allSelectable =
      selectableActivityIds.length + selectableFolderIds.length;
    const allSelected =
      allSelectable > 0 &&
      selectableActivityIds.every((id) => selectedIds.has(id)) &&
      selectableFolderIds.every((id) => selectedFolderIds.has(id));

    const moveDisabledReason =
      moveKind === null && total > 0
        ? selSubjects.length > 0
          ? "Subjects can't be moved — deselect them to move units or lessons."
          : "Mixed selection — select only lessons, or only units, to move."
        : null;

    return (
      <div
        data-testid="library-bulk-bar"
        className="ae-card mb-5 p-3 md:p-4 flex flex-wrap items-center justify-between gap-3 border-l-4 border-l-[#96ded1] bg-[#96ded1]/10"
      >
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-sm font-bold text-[#1c2d3d]" data-testid="library-bulk-count">
            {countLabel}
          </p>
          {allSelectable > 0 && (
            <button
              type="button"
              data-testid="library-bulk-select-all"
              onClick={() => {
                if (allSelected) {
                  setSelectedIds(new Set());
                  setSelectedFolderIds(new Set());
                } else {
                  setSelectedIds(new Set(selectableActivityIds));
                  setSelectedFolderIds(new Set(selectableFolderIds));
                }
              }}
              className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#4a6984] hover:text-[#1c2d3d] underline-offset-4 hover:underline"
            >
              {allSelected ? "Clear all" : "Select all"}
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                size="sm"
                disabled={!moveKind}
                title={moveDisabledReason || undefined}
                data-testid="library-bulk-move"
                className="bg-[#4a6984] hover:bg-[#1c2d3d] text-white disabled:opacity-40"
              >
                <FolderInput className="h-3.5 w-3.5 mr-1.5" /> Move to…
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="bg-white border-[#4a6984]/15 min-w-[210px]"
            >
              {moveKind === "activities" && (
                <>
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-[#4a6984]/70">
                    Move {actCount} lesson{actCount === 1 ? "" : "s"}
                  </DropdownMenuLabel>
                  <DropdownMenuItem
                    onSelect={() => handleBulkMove(null)}
                    data-testid="library-bulk-move-unfiled"
                    className="cursor-pointer"
                  >
                    <Inbox className="h-3.5 w-3.5 mr-2" /> Unfiled
                  </DropdownMenuItem>
                  {activityTargets.length > 0 && <DropdownMenuSeparator />}
                  {activityTargets.map((f) => (
                    <DropdownMenuItem
                      key={f.id}
                      onSelect={() => handleBulkMove(f.id)}
                      data-testid={`library-bulk-move-target-${f.id}`}
                      className="cursor-pointer"
                    >
                      <span
                        className="h-2.5 w-2.5 rounded-sm mr-2 shrink-0"
                        style={{ backgroundColor: f.color }}
                      />
                      <span className="truncate">{f.name}</span>
                    </DropdownMenuItem>
                  ))}
                </>
              )}
              {moveKind === "units" && (
                <>
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.18em] text-[#4a6984]/70">
                    Move {selUnits.length} unit{selUnits.length === 1 ? "" : "s"} to subject
                  </DropdownMenuLabel>
                  {subjectTargets.length === 0 && (
                    <p className="px-2 py-2 text-xs text-[#4a6984]/60">
                      Create a Subject folder first.
                    </p>
                  )}
                  {subjectTargets.map((f) => (
                    <DropdownMenuItem
                      key={f.id}
                      onSelect={() => bulkMoveUnits(selUnits.map((u) => u.id), f.id)}
                      data-testid={`library-bulk-move-unit-target-${f.id}`}
                      className="cursor-pointer"
                    >
                      <span
                        className="h-2.5 w-2.5 rounded-sm mr-2 shrink-0"
                        style={{ backgroundColor: f.color }}
                      />
                      <span className="truncate">{f.name}</span>
                    </DropdownMenuItem>
                  ))}
                </>
              )}
              {!moveKind && (
                <p className="px-2 py-2 text-xs text-[#4a6984]/60">
                  {moveDisabledReason || "Select lessons or units to move."}
                </p>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={total === 0}
            onClick={handleBulkDelete}
            data-testid="library-bulk-delete"
            className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 disabled:opacity-40"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
          </Button>
        </div>
      </div>
    );
  };

  const handleDelete = (a) => {
    // Phase 130 — Open the in-app dialog. The actual deletion runs
    // when the user confirms inside ConfirmDeleteDialog.
    setPendingDelete(a);
  };

  const performDelete = async (a) => {
    try {
      await deleteActivity(a.id);
      toast.success("Activity deleted");
      reload();
    } catch (e) {
      toast.error("Delete failed", { description: e?.message });
    }
  };

  const handleViewLessons = (label, activityIds) => {
    if (!activityIds || activityIds.length === 0) return;
    setLibraryFilter({ label, activityIds });
    setTab("library");
  };

  const handleDraftComment = (learningSkillName) => {
    if (!learningSkillName) return;
    setReportCardPrefill({
      learning_skill: learningSkillName,
      nonce: Date.now(),
    });
    setTab("report-cards");
  };

  const clearLibraryFilter = () => setLibraryFilter(null);

  // Apply filter when one is active. The filter list is what the
  // Tracker already pre-validated (taught activities only), so an empty
  // result here means everything was deleted — treat as no match.
  // When no Tracker filter is active, apply the folder filter instead.
  // Phase 124.E — when in the new browse landing, the items grid is
  // hidden entirely (replaced by Subject tiles), so we short-circuit
  // to an empty array.
  // Phase 122 — the platform-owned Math Engine folder id. All Lessons
  // excludes its contents; an active search still spans everything.
  const systemFolderId = folders.find((f) => f.system)?.id || null;
  const baseVisible = libraryFilter
    ? activities.filter((a) => libraryFilter.activityIds.includes(a.id))
    : selectedFolderId === "__browse__"
      ? []
      : selectedFolderId === null
        ? (searchQuery.trim()
            ? activities
            : activities.filter(
                (a) => !systemFolderId || a.folder_id !== systemFolderId
              ))
        : selectedFolderId === "__unfiled__"
          ? activities.filter((a) => !a.folder_id)
          : activities.filter((a) => a.folder_id === selectedFolderId);
  // Compose the readings-only filter on top of whatever folder /
  // tracker filter is currently active.
  const folderFilteredVisible = readingsOnly
    ? baseVisible.filter((a) => !!a.reading_markdown)
    : baseVisible;
  // Phase 123 — search filter. Tokenise the query and require every
  // token to match SOMEWHERE in the activity's text fields. Empty
  // query is a no-op so the search is opt-in.
  const visibleActivities = (() => {
    const q = (searchQuery || "").trim().toLowerCase();
    if (!q) return folderFilteredVisible;
    const tokens = q.split(/\s+/).filter(Boolean);
    return folderFilteredVisible.filter((a) => {
      const haystack = [
        a.activity_name,
        a.inputs?.grade,
        a.inputs?.subject,
        a.inputs?.strand,
        a.output_type,
        ...(a.curriculum_links || []),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return tokens.every((t) => haystack.includes(t));
    });
  })();
  // Count is computed off the BASE list so the chip can show the
  // total number of readings available in the current folder
  // (e.g. "Readings · 3 of 12") without flickering after the toggle.
  const readingsAvailableCount = baseVisible.filter(
    (a) => !!a.reading_markdown
  ).length;

  const total = coverage?.total_activities ?? 0;

  // Phase 115.8 — per-view title for the in-page top bar. The
  // /dashboard route now hosts five distinct surfaces (home, library,
  // community, planner, tracker, report-cards), each reached via a
  // ?view= query param. The top bar reflects whichever the teacher
  // is on so the rail's active state and the page heading line up.
  const VIEW_TITLES = {
    home: "Home",
    library: "My Library",
    community: "Community Library",
    tracker: "Coverage Tracker",
    planner: "Planner",
    "report-cards": "Report Card Builder",
  };
  const VIEW_SUBTITLES = {
    home: "Every activity you generate is auto-credited here.",
    library: `${total} total activities`,
    community: "Lessons shared by other teachers.",
    tracker: "Ontario expectations, learning skills, and transferable skills you've already taught.",
    planner: "Your week at a glance.",
    "report-cards": "Generate term and progress report comments.",
  };
  const shellTitle = VIEW_TITLES[tab] || "Home";
  const shellSubtitle = VIEW_SUBTITLES[tab] || "";

  return (
    <AppShell title={shellTitle} subtitle={shellSubtitle}>
    <div className="bg-[#f4f5f7] text-[#4a6984]">
      <main className="py-8 max-w-7xl mx-auto px-6">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          {/* Phase 115.1 — internal tab row removed. Each destination
              now has its own URL (?view=library, ?view=planner, etc.)
              driven from the left rail. The Tabs container is kept as
              a thin value-controlled wrapper so the TabsContent blocks
              below continue to render only the active view, but no
              navigation chrome is visible to the teacher. */}

          <TabsContent value="planner">
            <div className="flex items-center justify-end mb-3">
              <Link
                to="/long-range-plan"
                data-testid="planner-to-long-range-plan"
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md border border-[#4a6984]/25 bg-white text-[#1c2d3d] font-bold text-xs uppercase tracking-[0.1em] hover:bg-[#f4f5f7] hover:border-[#1c2d3d]/40"
              >
                <CalendarRange className="h-3.5 w-3.5" />
                Long Range Plan
                <ChevronRight className="h-3 w-3 ml-0.5" />
              </Link>
            </div>
            <Planner />
          </TabsContent>

          <TabsContent value="tracker">
            {loading ? (
              <p className="text-[#4a6984]/70">Loading tracker…</p>
            ) : (
              <CoverageTracker
                coverage={coverage}
                onViewLessons={handleViewLessons}
                onDraftComment={handleDraftComment}
              />
            )}
          </TabsContent>

          <TabsContent value="report-cards">
            <ReportCardGenerator prefill={reportCardPrefill} />
          </TabsContent>

          <TabsContent value="home" className="space-y-8">
            {/* Report-card seasonal nudge — Basic tier only. Renders only
                when `isBasicUser(user) === true` (strict tier match);
                Standard / Pro / Pro+ / admins are structurally excluded. */}
            {_rc_nudge_show_basic ? (
              <ReportCardNudgeBanner audience="basic" />
            ) : null}

            {/* Lane 2 (Feb 09 2026) — subscriber Home restructure.
                Structure: Today → Skill in the Wild → Recent work →
                Rings → Sample Plans. No stats-dump coverage cards —
                everything deeper lives on Coverage Tracker.

                Kill list (removed from Home only): Skill Focus,
                Ontario Learning Skills, Subjects, Grades, Strands,
                Ontario Curriculum Expectations Covered, Today's Pick.
                Guardrail check + Tracker mapping is documented in
                /app/memory/home_lane2_build_report.md. */}

            {/* 0. Planner preview — Pro-only read-only Day/Week/Month
                scroll-through that mirrors the Planner. Clickable (opens
                the full Planner) but not editable. Sits above Today. */}
            <HomePlannerPreview user={user} />

            {/* 1. Today card — every subscriber (Jun 2026). Today's
                scheduled planner lessons + summer / school-year empty
                states. */}
            <TodayCard user={user} />

            {/* 2. Skill in the Wild — the single skill-of-the-day
                surface. "Today's Pick" quick-action card was merged
                into this card's CTA per spec. Summer pill overrides
                the term label July + August. */}
            <section data-testid="home-skill-in-the-wild" className="ae-card overflow-hidden">
              <div className="grid grid-cols-1 md:grid-cols-[3fr_2fr] gap-0">
                <div className="p-6 md:p-8">
                  <SkillInTheWildCard refreshKey="home" />
                </div>
                <div className="hidden md:block border-l border-[#4a6984]/10">
                  <SkillInTheWildImage
                    refreshKey="home"
                    className="max-w-[320px] mx-auto"
                  />
                </div>
              </div>
            </section>

            {/* 3. Recent work — last 3 generated activities. Empty
                state carries forward the existing "No activities yet
                / Generate your first activity" treatment. */}
            <RecentWorkCard activities={activities} />

            {/* 4. Coverage snapshot — 7 transferable-skill rings,
                always all seven (zero-coverage skills render as
                empty tracks so the spine stays visible). All numbers
                come from the canonical coverage response — zero
                changes to `coverage.py`. */}
            {loading ? (
              <p className="text-[#4a6984]/70">Loading coverage…</p>
            ) : (
              <SkillRingsCard coverage={coverage} total={total} />
            )}

            {/* 5. Sample Plans — demoted from the old quick-actions
                strip; sits at the bottom of Home as a browse-on-a-
                slow-day entry point. */}
            <Link
              to="/samples"
              data-testid="dashboard-quick-sample-plans"
              className="group ae-card p-5 hover:shadow-md transition-shadow duration-200 flex items-start gap-4"
            >
              <div className="shrink-0 h-11 w-11 rounded-xl bg-[#f4843b]/20 border border-[#f4843b]/40 grid place-items-center">
                <Compass className="h-5 w-5 text-[#1c2d3d]" strokeWidth={2} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/70">
                  Inspiration
                </p>
                <h3 className="mt-0.5 font-extrabold text-lg text-[#1c2d3d]">
                  Sample Plans
                </h3>
                <p className="mt-1 text-sm text-[#4a6984] leading-snug">
                  Browse Ontario-aligned sample lessons, units, tests, and tasks. Copy any to your library.
                </p>
                <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.14em] text-[#1c2d3d] group-hover:gap-2 transition-all">
                  Browse samples <ArrowRight className="h-3.5 w-3.5" />
                </p>
              </div>
            </Link>
          </TabsContent>

          <TabsContent value="community">
            <CommunityTab />
          </TabsContent>

          <TabsContent value="library">
            {/* Phase 152.AA — Four-section library: Lessons & Units /
                Summative / Classroom Support / Individual Student
                Plans. Sticky segmented control sets the URL `section=`
                so deep-links land on the right pane. */}
            {(() => {
              const lessonsActivities = activities.filter(
                (a) => sectionForActivity(a) === "lessons",
              );
              const summativeActivities = activities.filter(
                (a) => sectionForActivity(a) === "summative",
              );
              return (
                <LibrarySectionTabs
                  section={librarySection}
                  counts={{
                    lessons: lessonsActivities.length,
                    summative: summativeActivities.length,
                  }}
                  onChange={changeLibrarySection}
                />
              );
            })()}

            {/* Classroom Support tab */}
            {librarySection === "support" && (
              <div className="space-y-4 mt-4" data-testid="library-support-pane">
                <ClassroomSupportPlansSection />
              </div>
            )}

            {/* Individual Student Plans tab */}
            {librarySection === "isps" && (
              <div className="space-y-4 mt-4" data-testid="library-isps-pane">
                <IspsLibrarySection />
              </div>
            )}

            {/* Supply Plans tab — founder-curated premade library. */}
            {librarySection === "supply" && (
              <div data-testid="library-supply-pane">
                <SupplyPlansLibrarySection />
              </div>
            )}

            {/* Summative tab — flat list, no folders/tiles. */}
            {librarySection === "summative" && (() => {
              const summativeActivities = activities.filter(
                (a) => sectionForActivity(a) === "summative",
              );
              if (loading) {
                return (
                  <p className="text-[#4a6984]/70 mt-4" data-testid="library-summative-loading">
                    Loading summative tasks…
                  </p>
                );
              }
              if (summativeActivities.length === 0) {
                return (
                  <div
                    className="ae-card p-10 text-center mt-4"
                    data-testid="library-summative-empty"
                  >
                    <h3 className="font-extrabold text-xl text-[#4a6984]">
                      No summative tasks yet
                    </h3>
                    <p className="mt-2 text-[#4a6984]/80">
                      Tasks you create in the Summative Task Designer save here automatically.
                    </p>
                    <Link
                      to="/summative"
                      data-testid="library-summative-empty-cta"
                      className="ae-cta inline-flex items-center gap-2 mt-5 px-5 py-2.5 rounded-md font-extrabold"
                    >
                      Create a summative task
                    </Link>
                  </div>
                );
              }
              return (
                <>
                  <LibraryRecentStrip
                    testid="library-summative-recent"
                    items={recentN(summativeActivities).map(activityToRecentItem)}
                  />
                  <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="library-summative-list">
                    {summativeActivities.map((a) => (
                      <LibraryCard
                        key={a.id}
                        activity={a}
                        folders={folders}
                        onDelete={handleDelete}
                        onActivityUpdate={(updated) =>
                          setActivities((list) =>
                            list.map((it) => (it.id === updated.id ? updated : it))
                          )
                        }
                        onMove={handleMove}
                      />
                    ))}
                  </div>
                </>
              );
            })()}

            {/* Lessons & Units tab — the original library experience,
                scoped to non-summative activities. Folders / Subject
                tiles / breadcrumb / drag-drop / multi-select stay
                exactly as they were before Phase 152.AA. */}
            {librarySection === "lessons" && (loading ? (
              <p className="text-[#4a6984]/70 mt-4">Loading library…</p>
            ) : activities.filter((a) => sectionForActivity(a) === "lessons").length === 0 ? (
              <div className="ae-card p-10 text-center mt-4" data-testid="library-empty">
                <h3 className="font-extrabold text-xl text-[#4a6984]">No lessons or units yet</h3>
                <p className="mt-2 text-[#4a6984]/80">
                  Generated lessons and units show up here. Summative tasks, classroom support plans, and individual student plans each have their own tab above.
                </p>
              </div>
            ) : (
              <div className="space-y-4 mt-4">
                {/* Phase 124.E — Collapsible "Folders" disclosure. Default
                    closed so the tile-browse view gets full width; teachers
                    who want the legacy tree expand it on demand. */}
                <details
                  open={folderSidebarOpen}
                  onToggle={(e) => setFolderSidebarOpen(e.currentTarget.open)}
                  className="ae-card overflow-hidden"
                  data-testid="library-folders-disclosure"
                >
                  <summary
                    data-testid="library-folders-disclosure-trigger"
                    className="cursor-pointer select-none px-4 py-2.5 flex items-center justify-between gap-3 hover:bg-[#f4f5f7] transition-colors"
                  >
                    <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#4a6984]">
                      <Library className="h-3.5 w-3.5" /> Folders
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wide text-[#4a6984]/70">
                      {folders.length} folder{folders.length === 1 ? "" : "s"}
                    </span>
                  </summary>
                  <div className="p-4 border-t border-[#4a6984]/15">
                    <FolderSidebar
                      folders={folders}
                      activities={activities}
                      selectedFolderId={libraryFilter ? null : selectedFolderId}
                      onSelect={(fid) => {
                        setSelectedFolderId(fid);
                        if (libraryFilter) setLibraryFilter(null);
                        if (selectMode) exitSelectMode();
                      }}
                      onCreate={handleCreateFolder}
                      onDropActivity={handleMove}
                      isDragging={!!draggingActivityId}
                      onUpdateFolder={handleUpdateFolder}
                      onDeleteFolder={handleDeleteFolder}
                      onReorderFolders={handleReorderFolders}
                    />
                  </div>
                </details>

                {/* Phase 124.E — Subject-tile browse landing. */}
                {selectedFolderId === "__browse__" && !libraryFilter ? (
                  <>
                    {selectMode &&
                      renderBulkBar({
                        selectableActivityIds: (activities || [])
                          .slice()
                          .sort((a, b) => (Date.parse(b?.created_at || "") || 0) - (Date.parse(a?.created_at || "") || 0))
                          .slice(0, 6)
                          .map((a) => a.id),
                        selectableFolderIds: folders
                          .filter((f) => !f.parent_folder_id && !f.system)
                          .map((f) => f.id),
                      })}
                    <SubjectTilesBrowse
                      folders={folders}
                      activities={activities}
                      unfiledCount={activities.filter((a) => !a.folder_id).length}
                      selectMode={selectMode}
                      onToggleSelectMode={selectMode ? exitSelectMode : () => setSelectMode(true)}
                      selectedFolderIds={selectedFolderIds}
                      onToggleSelectFolder={toggleSelectFolder}
                      selectedActivityIds={selectedIds}
                      onToggleSelectActivity={toggleSelect}
                      onSelectFolder={(fid) => {
                        setSelectedFolderId(fid);
                        if (selectMode) exitSelectMode();
                      }}
                      onCreateFolder={() => setFolderSidebarOpen(true)}
                      onAddUnitToSubject={handleCreateUnitUnderSubject}
                      onDeleteActivity={handleDelete}
                      onMoveActivity={handleMove}
                    />
                  </>
                ) : (
                <div className="min-w-0">
                  {!libraryFilter ? (
                    <button
                      type="button"
                      data-testid="library-back-to-browse"
                      onClick={() => setSelectedFolderId("__browse__")}
                      className="mb-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-[0.14em] text-[#1c2d3d] border border-[#4a6984]/25 bg-white hover:bg-[#96ded1]/20 hover:border-[#96ded1] transition-colors"
                    >
                      <ArrowLeft className="h-3.5 w-3.5" /> Library
                    </button>
                  ) : null}
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#4a6984]/70" data-testid="library-breadcrumb">
                      {libraryFilter
                        ? "Filtered view"
                        : (() => {
                            const current = folders.find((f) => f.id === selectedFolderId);
                            const breadcrumb =
                              selectedFolderId === null
                                ? "All lessons"
                                : selectedFolderId === "__unfiled__"
                                  ? "Unfiled"
                                  : current
                                    ? current.parent_folder_id
                                      ? (() => {
                                          const parent = folders.find((f) => f.id === current.parent_folder_id);
                                          return parent ? (
                                            <>
                                              <button
                                                type="button"
                                                data-testid={`breadcrumb-parent-${parent.id}`}
                                                onClick={() => setSelectedFolderId(parent.id)}
                                                className="hover:text-[#1c2d3d] underline-offset-4 hover:underline"
                                              >
                                                {parent.name}
                                              </button>
                                              <span className="mx-1.5 text-[#4a6984]/40">›</span>
                                              <span className="text-[#1c2d3d]">{current.name}</span>
                                            </>
                                          ) : current.name;
                                        })()
                                      : current.name
                                    : "Folder";
                            return (
                              <>
                                <button
                                  type="button"
                                  data-testid="library-breadcrumb-browse"
                                  onClick={() => setSelectedFolderId("__browse__")}
                                  className="hover:text-[#1c2d3d] underline-offset-4 hover:underline"
                                >
                                  Library
                                </button>
                                <span className="mx-1.5 text-[#4a6984]/40">›</span>
                                {breadcrumb}
                              </>
                            );
                          })()}
                      <span className="ml-2 text-[#4a6984]/50">·</span>
                      <span className="ml-2 normal-case tracking-normal text-[#4a6984]/70">
                        {visibleActivities.length} item{visibleActivities.length === 1 ? "" : "s"}
                      </span>
                    </p>
                    <div className="flex items-center gap-2">
                      {/* Phase 123 — Library search. Searches activity
                          names, grade/subject/strand, and curriculum
                          codes across the current folder filter. */}
                      <div className="relative">
                        <input
                          type="search"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search library…"
                          data-testid="library-search-input"
                          aria-label="Search library"
                          className="h-8 w-44 sm:w-56 rounded-full border border-[#4a6984]/20 bg-white pl-8 pr-3 text-xs font-medium text-[#1c2d3d] placeholder:text-[#4a6984]/60 focus:outline-none focus:ring-2 focus:ring-[#96ded1]"
                        />
                        <svg
                          className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#4a6984]/70 pointer-events-none"
                          viewBox="0 0 16 16"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="7" cy="7" r="5" />
                          <line x1="11" y1="11" x2="14" y2="14" />
                        </svg>
                        {searchQuery && (
                          <button
                            type="button"
                            onClick={() => setSearchQuery("")}
                            data-testid="library-search-clear"
                            aria-label="Clear search"
                            className="absolute right-1.5 top-1/2 -translate-y-1/2 h-5 w-5 rounded-full text-[#4a6984] hover:bg-[#f4f5f7] flex items-center justify-center"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                      {(readingsAvailableCount > 0 || readingsOnly) && (
                        <button
                          type="button"
                          onClick={() => setReadingsOnly((v) => !v)}
                          data-testid="library-filter-readings"
                          className={
                            "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.14em] transition-colors " +
                            (readingsOnly
                              ? "bg-[#96ded1] border-[#1c2d3d]/30 text-[#1c2d3d]"
                              : "bg-white border-[#4a6984]/20 text-[#4a6984] hover:bg-[#f4f5f7]")
                          }
                          title={readingsOnly ? "Show all lessons" : "Show only lessons with a generated Reading"}
                        >
                          <BookOpen className="h-3 w-3" />
                          {readingsOnly ? "Showing readings" : "Readings"}
                          <span className="ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-4 px-1 rounded-full bg-[#1c2d3d]/10 text-[10px] font-bold text-[#1c2d3d]">
                            {readingsAvailableCount}
                          </span>
                        </button>
                      )}
                      {(() => {
                        const childUnitsInView = folders.filter(
                          (f) => f.parent_folder_id === selectedFolderId && !f.system
                        );
                        const canSelect =
                          (visibleActivities.length > 0 || childUnitsInView.length > 0) &&
                          !libraryFilter;
                        if (!canSelect) return null;
                        return (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            data-testid={selectMode ? "library-select-cancel" : "library-select-enter"}
                            onClick={selectMode ? exitSelectMode : () => setSelectMode(true)}
                            className="border-[#4a6984]/20 text-[#4a6984] hover:bg-[#f4f5f7]"
                          >
                            {selectMode ? (
                              <><X className="h-3.5 w-3.5 mr-1.5" /> Cancel</>
                            ) : (
                              <><CheckSquare className="h-3.5 w-3.5 mr-1.5" /> Select</>
                            )}
                          </Button>
                        );
                      })()}
                    </div>
                  </div>
                  {selectMode &&
                    renderBulkBar({
                      selectableActivityIds: visibleActivities.map((a) => a.id),
                      selectableFolderIds: folders
                        .filter((f) => f.parent_folder_id === selectedFolderId && !f.system)
                        .map((f) => f.id),
                    })}
                  {libraryFilter && (
                    <div
                      data-testid="library-filter-banner"
                      className="ae-card flex items-center justify-between gap-4 flex-wrap p-4 mb-5 border-l-4 border-l-[#96ded1]"
                    >
                      <div className="min-w-0">
                        <p className="font-bold uppercase text-[10px] tracking-[0.2em] text-[#4a6984]/70">
                          Filtered
                        </p>
                        <p className="mt-0.5 text-sm font-semibold text-[#1c2d3d] tracking-tight">
                          Showing {visibleActivities.length} of {activities.length} —{" "}
                          <span className="text-[#4a6984]">{libraryFilter.label}</span>
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={clearLibraryFilter}
                        data-testid="library-filter-clear"
                        className="border-[#4a6984]/20 text-[#4a6984] hover:bg-[#f4f5f7] hover:text-[#4a6984]"
                      >
                        Clear filter
                      </Button>
                    </div>
                  )}
                  {/* Phase 122 — Math Engine system folder copy. Coverage
                      tracking IS built (unit overview), so we point there. */}
                  {(() => {
                    const current = folders.find((f) => f.id === selectedFolderId);
                    if (!current || !current.system) return null;
                    return (
                      <div
                        data-testid="library-math-engine-folder-note"
                        className="ae-card mb-5 p-4 border-l-4 border-l-[#7c5cbf] bg-[#7c5cbf]/5"
                      >
                        <p className="text-sm text-[#1c2d3d]">
                          These are your Math Engine activities. They're scheduled and
                          tracked differently from regular lessons — their progress lives
                          in the Math Engine, on each unit's overview.
                        </p>
                      </div>
                    );
                  })()}
                  {/* Phase 124.E — Child Unit tiles ABOVE loose-in-Subject items. */}
                  {(() => {
                    const current = folders.find((f) => f.id === selectedFolderId);
                    if (!current || current.parent_folder_id || current.system) return null;
                    const childUnits = folders.filter((f) => f.parent_folder_id === selectedFolderId);
                    return (
                      <UnitTilesStrip
                        units={childUnits}
                        activities={activities}
                        subjectName={current.name}
                        selectMode={selectMode}
                        selectedFolderIds={selectedFolderIds}
                        onToggleSelectFolder={toggleSelectFolder}
                        onSelectFolder={(fid) => {
                          setSelectedFolderId(fid);
                          if (selectMode) exitSelectMode();
                        }}
                        onAddUnit={(name, color) =>
                          handleCreateUnitUnderSubject(current.id, name, color)
                        }
                        onDeleteFolder={handleDeleteFolder}
                      />
                    );
                  })()}
                  {visibleActivities.length === 0 ? (
                    <div className="ae-card p-10 text-center">
                      <h3 className="font-extrabold text-lg text-[#4a6984]">
                        {searchQuery.trim()
                          ? `No results for "${searchQuery.trim()}"`
                          : readingsOnly
                            ? "No lessons with a Reading in this folder yet"
                            : libraryFilter
                              ? "No saved lessons match this filter"
                              : selectedFolderId === "__unfiled__"
                                ? "Nothing in Unfiled"
                                : "This folder is empty"}
                      </h3>
                      <p className="mt-2 text-sm text-[#4a6984]/75">
                        {searchQuery.trim()
                          ? "Try a different keyword, or clear search to see everything."
                          : libraryFilter
                            ? "They may have been deleted. Clear the filter to see everything."
                            : "Move lessons in using the Move to… button on each lesson card, or generate a new one from the Tracker tab."}
                      </p>
                    </div>
                  ) : (
                    <div
                      className="grid grid-cols-1 md:grid-cols-2 gap-5"
                      data-testid="library-list"
                    >
                      {visibleActivities.map((a) => (
                        <LibraryCard
                          key={a.id}
                          activity={a}
                          folders={folders}
                          onDelete={handleDelete}
                          onMove={handleMove}
                          onDragStart={(id) => setDraggingActivityId(id)}
                          onDragEnd={() => setDraggingActivityId(null)}
                          selectMode={selectMode}
                          selected={selectedIds.has(a.id)}
                          onToggleSelect={toggleSelect}
                          onActivityUpdate={(updated) =>
                            setActivities((list) =>
                              list.map((it) => (it.id === updated.id ? updated : it))
                            )
                          }
                          onScheduleUnit={(unit) => setSchedulingUnit(unit)}
                          onScheduleLesson={(act) => setSchedulingLesson(act)}
                        />
                      ))}
                    </div>
                  )}
                </div>
                )}
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </main>
      <ScheduleUnitDialog
        open={!!schedulingUnit}
        onOpenChange={(o) => !o && setSchedulingUnit(null)}
        activity={schedulingUnit}
        onScheduled={() => { setSchedulingUnit(null); setTab("planner"); }}
      />
      {/* Phase 88 — single-period scheduler. Reuses the post-gen popup
          so Math Engine lessons (and any single-period activity) drop
          straight onto the next available subject slot. */}
      {schedulingLesson && (
        <PostGenScheduleDialog
          key={schedulingLesson.id}
          activity={schedulingLesson}
          onDismiss={() => setSchedulingLesson(null)}
        />
      )}
      {/* Phase 123 — cascade delete confirmation for non-empty folders. */}
      <CascadeDeleteDialog
        open={!!cascadeDeleteFolder}
        folder={cascadeDeleteFolder?.folder}
        activityCount={cascadeDeleteFolder?.activityCount || 0}
        childFolderCount={cascadeDeleteFolder?.childFolderCount || 0}
        onCancel={() => setCascadeDeleteFolder(null)}
        onConfirm={(mode) =>
          _performCascadeDelete(cascadeDeleteFolder.folder.id, mode)
        }
      />
      {/* Batch cascade delete — multi-select of Subject/Unit folders. */}
      <CascadeDeleteDialog
        open={!!batchCascade}
        folder={null}
        batchCount={batchCascade?.folderIds?.length || 0}
        activityCount={batchCascade?.activityCount || 0}
        childFolderCount={batchCascade?.childFolderCount || 0}
        extraLessonCount={batchCascade?.extraActivityIds?.length || 0}
        onCancel={() => setBatchCascade(null)}
        onConfirm={(mode) => performBatchCascade(mode)}
      />
      {/* Phase 130 — Library item delete confirmation. Reliable
          across mobile + PC; replaces window.confirm() which is
          silently blocked on some mobile WebViews. Handles both
          single-card delete (`pendingDelete` is an activity object)
          and bulk delete (`pendingDelete = {bulk: true, ids: [...]}`). */}
      <ConfirmDeleteDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title={
          pendingDelete?.bulk
            ? `Delete ${pendingDelete.ids.length} lesson${
                pendingDelete.ids.length === 1 ? "" : "s"
              }?`
            : `Delete "${pendingDelete?.activity_name || "this lesson"}"?`
        }
        description={
          pendingDelete?.bulk
            ? `This permanently removes ${pendingDelete.ids.length} lesson${
                pendingDelete.ids.length === 1 ? "" : "s"
              } from your library. This cannot be undone.`
            : "This permanently removes the lesson, its student materials, and any scheduled planner blocks. This cannot be undone."
        }
        confirmLabel={
          pendingDelete?.bulk
            ? `Delete ${pendingDelete.ids.length}`
            : "Delete lesson"
        }
        testidPrefix={
          pendingDelete?.bulk
            ? "library-bulk-delete"
            : `library-delete-${pendingDelete?.id || "unknown"}`
        }
        onConfirm={async () => {
          if (!pendingDelete) return;
          if (pendingDelete.bulk) {
            await performBulkDelete(pendingDelete.ids);
          } else {
            await performDelete(pendingDelete);
          }
        }}
      />
    </div>
    </AppShell>
  );
};

export default Dashboard;
